import {useEffect, useRef, useState} from 'react';
import {Button, Col, Form, Row, Spinner} from 'react-bootstrap';
import {useMutation} from '@tanstack/react-query';
import {Link, useNavigate} from 'react-router-dom';
import Reaptcha from 'reaptcha';
import {useForm} from 'react-hook-form';
import useCharacterCount from '../../../hooks/useCharacterCount';
import apiClient, {userSignUp} from '../../../api/apiClient';
import {useAuth} from '../../../context/AuthContext';
import {useModal} from '../../../context/ModalContext';
import {useAlert} from '../../../context/AlertContext';
import GoogleAnalytics from '../../google-analytics/GoogleAnalytics';
import Classes from '../../join-form/JoinForm.module.scss';
//== icons
import showPassIcon from '../../../assets/icons/showPass.svg';
import closePassIcon from '../../../assets/icons/closePass.svg';

const SignUp = () => {
    // const {setShowSignIn} = props;
    const [showPassword, setShowPassword] = useState(false);
    const [checkEmail, setCheckEmail] = useState(false);
    const [checkUserName, setCheckUserName] = useState(false);
    const {register, handleSubmit, formState: {errors}, reset, watch}: any = useForm();
    const navigate = useNavigate();
    const {setStoredToken} = useAuth();
    const {handleJoinFormClose} = useModal();
    const {setAddAlert} = useAlert();
    //== How many character left
    const [characterCount, inputPopup, charCountHandler] = useCharacterCount(process.env.REACT_APP_USER_NAME_MAX_CHAR_LIMIT);

    //== Password show hide
    const handleShowPassword = (e: any) => {
        e.preventDefault();
        setShowPassword(!showPassword);
    }

    //== Check matched password
    const password = useRef();
    password.current = watch("password", "");

    //==Google recaptcha
    const [isRecaptchaVerified, setRecaptchaVerified] = useState(false);
    const [showRecaptchaMessage, setShowRecaptchaMessage] = useState(false);
    const [isUserNameValid, setUserNameValid] = useState(false);
    const [isEmailValid, setEmailValid] = useState(false);

    // Email Used Message
    const [usedMessage, setUsedMessage] = useState(false);
    // Username Used Message
    const [usedUserNameMessage, setUsedUserNameMessage] = useState(false);

    //== Loading State
    const captchaOnVerify = () => {
        setRecaptchaVerified(true);
        setShowRecaptchaMessage(false);
    }

    // Email Validity Check
    const emailValidityCheck = async (email: any) => {
        handleCheckEmail(email)
            .then((apiResponse: any) => {
                if (apiResponse.data.emailUsed === true) {
                    setEmailValid(false);
                    setUsedMessage(true);
                } else if (apiResponse.data.emailUsed === false) {
                    setEmailValid(true);
                    setUsedMessage(false);
                }
            })
            .catch((error:any) => {
                console.log(error);
            });
    };

    // Username Validity Check
    const userNameValidityCheck = async (userName: any) => {
        handleCheckUserName(userName)
            .then((apiResponse: any) => {
                if (apiResponse.data.userNameUsed === true) {
                    setUserNameValid(false);
                    setUsedUserNameMessage(true);
                } else if (apiResponse.data.userNameUsed === false) {
                    setUserNameValid(true);
                    setUsedUserNameMessage(false);
                }
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const handleCheckUserName = (userName: any) => {
        return new Promise((resolve, reject) => {
            apiClient.get(`${process.env.REACT_APP_END_POINT_CHECK_USER_NAME}/${userName}`)
                .then((apiResponse) => {
                    resolve(apiResponse);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    const handleCheckEmail = (email: any) => {
        return new Promise((resolve, reject) => {
            apiClient.get(`${process.env.REACT_APP_END_POINT_CHECK_EMAIL}/${email}`)
                .then((apiResponse) => {
                    resolve(apiResponse);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    //== Handle user signup
    const {mutate, isLoading}: any = useMutation(userSignUp);
    const handleSignUp = async (signUpData: any) => {
        try {
            if (!isEmailValid) {
                await emailValidityCheck(signUpData.email);
            }
            if (!isUserNameValid) {
                await userNameValidityCheck(signUpData.userName);
            }

            if (!isRecaptchaVerified) {
                setShowRecaptchaMessage(true);
            }

            if (isEmailValid && isUserNameValid && isRecaptchaVerified) {
                await mutate({
                    userName: signUpData.userName,
                    email: signUpData.email,
                    password: signUpData.password
                }, {
                    onSuccess: () => {
                        GoogleAnalytics.sendEvent(
                            'User create account',
                            'Signup for new account',
                            'User successfully create account'
                        );
                        apiClient.post(`${process.env.REACT_APP_END_POINT_LOGIN}`, {
                            email: signUpData.email,
                            password: signUpData.password,
                        }).then((signUpResUser) => {
                            if (signUpResUser?.data?.status === 200) {
                                setStoredToken(signUpResUser?.data?.jwt);
                                reset();
                                handleJoinFormClose();
                                navigate('/dashboard/account-settings')
                                setAddAlert({
                                    alertType: 'default',
                                    alertMessage: 'Account created successfully'
                                })
                            }
                        });
                    },
                    onError: (err:any) => {
                        GoogleAnalytics.sendEvent(
                            'User create account',
                            'Signup for new account',
                            `User failed create account with ${err?.message}`
                        );
                        setAddAlert({
                            alertType: 'error',
                            alertMessage: 'Account created Failed'
                        });
                    }
                });
            }
        } catch (err: any) {
            GoogleAnalytics.sendEvent(
                'User create account',
                'Signup for new account',
                `User failed create account with ${err?.message}`
            );
        }
    }

    useEffect(() => {
        reset();
        // eslint-disable-next-line
    }, []);

    return (
        <Form
            className={'text-start'}
            onSubmit={handleSubmit(handleSignUp)}
        >
            <Form.Group controlId={'userName'}>
                <Form.Control
                    type="text"
                    placeholder="Enter User Name"
                    className={`${errors.userName ? 'error-input' : ''}`}
                    maxLength={Number(process.env.REACT_APP_USER_NAME_MAX_CHAR_LIMIT)}
                    {...register('userName', {
                        required: true,
                        onChange: (e: any) => {
                            charCountHandler(e);
                            let val = e.target.value.trim();
                            e.target.value = val.replace(' ', '').toLocaleLowerCase();
                            userNameValidityCheck(e.target.value)
                            if (val.length >= 1) {
                                setCheckUserName(true);
                            } else {
                                setCheckUserName(false);
                            }
                        }
                    })}
                />
                {inputPopup && (<p className={'error-input-message'}>character left: {characterCount}</p>)}
                {(usedUserNameMessage && checkUserName) ? (<p className="error-input-message">This username is currently unavailable. Try another one?</p>) : ''}
            </Form.Group>
            <Form.Group controlId={'signupEmail'}>
                <Form.Control
                    type="email"
                    placeholder="Enter email"
                    className={`${errors.email ? 'error-input' : ''}`}
                    {...register('email', {
                        required: true,
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                            message: "Enter a valid e-mail address",
                        },
                        onChange: (e: any) => {
                            let email = e.target.value;
                            let regx = new RegExp(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i);
                            if (regx.test(email)) {
                                emailValidityCheck(email)
                                setCheckEmail(true);
                            } else {
                                setCheckEmail(false);
                            }
                        }
                    })}
                />
                {errors.email && (<p className={'error-input-message'}>{errors.email.message}</p>)}
                {(usedMessage && checkEmail) && (<p className={'error-input-message'}>This email is currently unavailable. Try another one?</p>)}
            </Form.Group>

            <Row>
                <Col className={'pe-1'}>
                    <Form.Group className={`${Classes.passwordGroup}`} controlId={'signupPassword'}>
                        <Form.Control
                            type={showPassword ? 'text' : 'password'}
                            className={`${errors.password ? 'error-input' : ''}`}
                            ref={password}
                            placeholder="Password"
                            {...register('password', {
                                required: true,
                                pattern: {
                                    value: /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{6,}$/,
                                    message: 'Password should be: at least 6 characters, lowercase, uppercase, number and symbol'
                                }
                            })}
                        />
                        <button className={Classes.showPassBtn} onClick={handleShowPassword} type={'button'}>
                            {showPassword ?
                                (<img src={showPassIcon} alt="Show"/>)
                                : (<img src={closePassIcon} alt="Close"/>)}
                        </button>
                    </Form.Group>
                    {errors.password && (<p className={'error-input-message mt-0'}>{errors.password.message}</p>)}
                </Col>
                <Col className={'ps-1'}>
                    <Form.Group className={`${Classes.passwordGroup}`} controlId={'confirmPassword'}>
                        <Form.Control
                            type={showPassword ? 'text' : 'password'}
                            className={`${errors.confirmPassword ? 'error-input' : ''}`}
                            placeholder="Retype Password"
                            {...register('confirmPassword', {
                                validate: (value: any) => {
                                    return value === password.current || "The passwords do not match"
                                }
                            })}
                        />
                        <button className={Classes.showPassBtn} onClick={handleShowPassword} type={'button'}>
                            {showPassword ?
                                (<img src={showPassIcon} alt="Show"/>)
                                : (<img src={closePassIcon} alt="Close"/>)}
                        </button>
                    </Form.Group>
                    {errors.confirmPassword && (<p className={'error-input-message'}>{errors.confirmPassword.message}</p>)}
                </Col>
            </Row>






            <div className={Classes.recaptchaBox}>
                <div>
                    <Reaptcha
                        sitekey="6Lcz6I0aAAAAAJxmgXk1CK-GtxnEOAouy8vTAMSG"
                        onVerify={captchaOnVerify}
                        theme={'dark'}
                    />
                    {showRecaptchaMessage && (
                        <p className="error-input-message">Are you a robot?</p>
                    )}
                </div>
            </div>

            <Form.Group className={Classes.acceptCheckBox} controlId={'acceptPrivacyPolicy'}>
                <Form.Check
                    label={
                        <>I have read, understand, and agree to the <Link to={'#'}>Privacy
                            Policy </Link> and <Link to={'/terms-conditions'} target={'_blank'}>Terms of Service</Link></>
                    }
                    {...register('PrivacyPolicyCheck', {
                        required: 'You must agree before submitting.',
                    })}
                />
                {errors.PrivacyPolicyCheck && (
                    <p className={'error-input-message'}>{errors.PrivacyPolicyCheck.message}</p>)}
            </Form.Group>
            <Button
                variant="secondary"
                type="submit"
                bsPrefix={'g-btn'}
                className={`${Classes.submitBtn} w-100`}
            >
                {isLoading ? (<Spinner animation="border" size="sm" />) : 'Continue'}
            </Button>
        </Form>
    );
};

export default SignUp;